// src/views/Shared/ClientProfile.js

import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { db } from '../../firebase';
import {
  collection,
  getDocs,
  query,
  where,
  updateDoc,
  doc,
  getDoc,
  limit,
  addDoc,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from 'firebase/storage';

const FUNCTIONS_BASE = 'https://us-central1-tarjeta-ad945.cloudfunctions.net';

const ClientProfile = () => {
  const { clientId } = useParams();
  const [profile, setProfile] = useState(null);
  const [editData, setEditData] = useState({});
  const [resolvedDocId, setResolvedDocId] = useState(null);
  const [status, setStatus] = useState(null);
  const [imageUpload, setImageUpload] = useState(null);
  const [imageURL, setImageURL] = useState('');
  const [eventForm, setEventForm] = useState({
    title: '',
    date: '',
    time: '',
    duration: '60',
    notes: '',
  });
  const [eventStatus, setEventStatus] = useState(null);
  const [eventLoading, setEventLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  const loadClientEvents = useCallback(async (clientDocId) => {
    if (!clientDocId) return;
    setEventsLoading(true);
    try {
      const eventsRef = collection(db, 'clients', clientDocId, 'events');
      const eventsSnap = await getDocs(
        query(eventsRef, orderBy('start', 'asc'), limit(20))
      );
      const docs = eventsSnap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      setEvents(docs);
    } catch (err) {
      console.error('❌ Error al cargar eventos del cliente:', err);
    } finally {
      setEventsLoading(false);
    }
  }, []);

  // Load profile
  useEffect(() => {
    const loadProfile = async () => {
      const auth = getAuth();
      const user = auth.currentUser;
      const candidate = clientId || user?.email?.toLowerCase();
      if (!candidate) return;

      const candidateId = decodeURIComponent(candidate);
      let docRef = doc(db, 'clients', candidateId);
      let docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        const normalizedEmail = candidateId.includes('@') ? candidateId.toLowerCase() : null;
        const searches = [];
        if (normalizedEmail) {
          searches.push(where('email', '==', normalizedEmail));
        }
        searches.push(where('uid', '==', candidateId));

        let found = null;
        for (const condition of searches) {
          const qSnap = await getDocs(query(collection(db, 'clients'), condition, limit(1)));
          if (!qSnap.empty) {
            found = qSnap.docs[0];
            break;
          }
        }

        if (!found) {
          console.warn('❗ Perfil no encontrado');
          return;
        }

        docSnap = found;
        docRef = doc(db, 'clients', found.id);
      }

      const data = docSnap.data();

      setProfile(data);
      setEditData(data);
      setResolvedDocId(docSnap.id);
      setImageURL(data?.profileImageUrl || '');
    };

    loadProfile();
  }, [clientId]);

  useEffect(() => {
    if (!resolvedDocId) return;
    loadClientEvents(resolvedDocId);
  }, [resolvedDocId, loadClientEvents]);

  const handleChange = (e) => {
    setEditData({ ...editData, [e.target.name]: e.target.value });
  };

  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      setImageUpload(e.target.files[0]);
    }
  };

  const handleImageUpload = async () => {
    if (!imageUpload || !resolvedDocId) return;

    const storage = getStorage();
    const imgRef = storageRef(storage, `clients/${resolvedDocId}/profile.jpg`);

    try {
      await uploadBytes(imgRef, imageUpload);
      const downloadURL = await getDownloadURL(imgRef);
      setImageURL(downloadURL);

      await updateDoc(doc(db, 'clients', resolvedDocId), {
        profileImageUrl: downloadURL,
      });

      setStatus('success');
    } catch (err) {
      console.error('❌ Error subiendo imagen:', err);
      setStatus('error');
    }
  };

  const handleSave = async () => {
    if (!resolvedDocId) return;

    try {
      const ref = doc(db, 'clients', resolvedDocId);
      await updateDoc(ref, editData);
      setStatus('success');
    } catch (err) {
      console.error('❌ Error al guardar:', err);
      setStatus('error');
    }
  };

  const handleEventInputChange = (e) => {
    const { name, value } = e.target;
    setEventForm((prev) => ({ ...prev, [name]: value }));
    if (eventStatus) {
      setEventStatus(null);
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    if (!resolvedDocId) return;

    const trimmedTitle = eventForm.title.trim();
    const recipientEmail = (profile?.email || editData.email || '').trim();

    if (!trimmedTitle || !eventForm.date || !eventForm.time) {
      setEventStatus({ type: 'error', message: 'Completa el título, la fecha y la hora del evento.' });
      return;
    }

    if (!recipientEmail) {
      setEventStatus({ type: 'error', message: 'Este cliente necesita un correo para agendar la cita.' });
      return;
    }

    const startDateTime = new Date(`${eventForm.date}T${eventForm.time}`);
    if (Number.isNaN(startDateTime.getTime())) {
      setEventStatus({ type: 'error', message: 'Fecha u hora inválidas.' });
      return;
    }

    const minutes = Math.max(parseInt(eventForm.duration, 10) || 60, 15);
    const endDateTime = new Date(startDateTime.getTime() + minutes * 60000);
    const descriptionBody =
      eventForm.notes.trim() ||
      `Visita con ${profile?.businessName || profile?.name || 'cliente'}`;

    const payload = {
      title: trimmedTitle,
      description: descriptionBody,
      start: startDateTime.toISOString(),
      end: endDateTime.toISOString(),
      email: recipientEmail,
    };

    setEventLoading(true);
    setEventStatus(null);
    try {
      const response = await fetch(`${FUNCTIONS_BASE}/createCalendarEvent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(errorBody || 'No se pudo crear el evento');
      }

      const responseJson = await response.json().catch(() => ({}));
      const calendarLink = responseJson.link || responseJson.htmlLink || null;

      await addDoc(collection(db, 'clients', resolvedDocId, 'events'), {
        title: payload.title,
        description: descriptionBody,
        start: payload.start,
        end: payload.end,
        calendarLink,
        createdAt: serverTimestamp(),
        createdBy: getAuth().currentUser?.email || 'backoffice',
      });

      setEventStatus({ type: 'success', message: 'Evento agendado y sincronizado con el calendario.' });
      setEventForm({ title: '', date: '', time: '', duration: '60', notes: '' });
      loadClientEvents(resolvedDocId);
    } catch (err) {
      console.error('❌ Error al crear evento:', err);
      setEventStatus({ type: 'error', message: 'No se pudo agendar el evento. Intenta nuevamente.' });
    } finally {
      setEventLoading(false);
    }
  };

  const formatEventDate = (value) => {
    if (!value) return 'Sin fecha';
    const dateValue = typeof value.toDate === 'function' ? value.toDate() : new Date(value);
    if (!(dateValue instanceof Date) || Number.isNaN(dateValue.getTime())) {
      return 'Sin fecha';
    }
    return dateValue.toLocaleString('es-PR', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  if (!profile) return <p className="text-center text-gray-500 mt-10">Cargando perfil...</p>;

  return (
    <div className="max-w-3xl mx-auto mt-10 bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
      <h2 className="text-2xl font-semibold text-gray-800 dark:text-white mb-6">Perfil del Cliente</h2>

      {/* Estado */}
      {status === 'success' && (
        <div className="mb-4 text-green-700 bg-green-100 px-4 py-2 rounded-lg">
          ✅ Perfil actualizado correctamente
        </div>
      )}
      {status === 'error' && (
        <div className="mb-4 text-red-700 bg-red-100 px-4 py-2 rounded-lg">
          ❌ Hubo un error al guardar
        </div>
      )}

      {/* Imagen de perfil */}
      <div className="mb-6 text-center">
        <img
          src={imageURL || '/default-avatar.png'}
          alt="Imagen de perfil"
          className="mx-auto w-32 h-32 rounded-full object-cover border-4 border-gray-300 dark:border-gray-600"
        />
        <div className="mt-3">
          <input
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            className="block w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 cursor-pointer focus:outline-none dark:text-gray-400 dark:bg-gray-700 dark:border-gray-600"
          />
          <button
            onClick={handleImageUpload}
            className="mt-2 inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 dark:bg-blue-500 dark:hover:bg-blue-600 focus:outline-none dark:focus:ring-blue-800"
          >
            Subir Imagen
          </button>
        </div>
      </div>

      {/* Campos del perfil */}
      <form className="space-y-5">
        <div>
          <label htmlFor="businessName" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
            Nombre del Negocio
          </label>
          <input
            type="text"
            name="businessName"
            id="businessName"
            value={editData.businessName || ''}
            onChange={handleChange}
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
          />
        </div>

        <div>
          <label htmlFor="phone" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
            Teléfono
          </label>
          <input
            type="text"
            name="phone"
            id="phone"
            value={editData.phone || ''}
            onChange={handleChange}
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
          />
        </div>

        <div>
          <label htmlFor="email" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
            Correo electrónico
          </label>
          <input
            type="email"
            name="email"
            id="email"
            value={editData.email || ''}
            onChange={handleChange}
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
          />
        </div>

        <div>
          <label htmlFor="address" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
            Dirección
          </label>
          <input
            type="text"
            name="address"
            id="address"
            value={editData.address || ''}
            onChange={handleChange}
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
          />
        </div>

        <button
          type="button"
          onClick={handleSave}
          className="w-full mt-4 text-white bg-green-600 hover:bg-green-700 focus:ring-4 focus:outline-none focus:ring-green-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-green-500 dark:hover:bg-green-600 dark:focus:ring-green-800"
        >
          Guardar Cambios
        </button>
      </form>

      <section className="mt-10 bg-gray-50 border border-gray-200 rounded-lg p-6">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Eventos y visitas</h3>
            <p className="text-sm text-gray-500">Registra citas y sincronízalas automáticamente con Google Calendar.</p>
          </div>
        </div>

        {eventStatus && (
          <div
            className={`mt-3 rounded-md px-4 py-2 text-sm ${
              eventStatus.type === 'success'
                ? 'bg-green-50 text-green-700'
                : 'bg-red-50 text-red-700'
            }`}
          >
            {eventStatus.message}
          </div>
        )}

        <form className="mt-4 grid gap-4 md:grid-cols-2" onSubmit={handleCreateEvent}>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700">Título del evento</label>
            <input
              type="text"
              name="title"
              value={eventForm.title}
              onChange={handleEventInputChange}
              className="mt-1 w-full rounded-lg border border-gray-300 p-2 text-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="Visita del equipo, entrega, reunión..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Fecha</label>
            <input
              type="date"
              name="date"
              value={eventForm.date}
              onChange={handleEventInputChange}
              className="mt-1 w-full rounded-lg border border-gray-300 p-2 text-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Hora</label>
            <input
              type="time"
              name="time"
              value={eventForm.time}
              onChange={handleEventInputChange}
              className="mt-1 w-full rounded-lg border border-gray-300 p-2 text-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Duración</label>
            <select
              name="duration"
              value={eventForm.duration}
              onChange={handleEventInputChange}
              className="mt-1 w-full rounded-lg border border-gray-300 p-2 text-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="30">30 min</option>
              <option value="45">45 min</option>
              <option value="60">60 min</option>
              <option value="90">90 min</option>
              <option value="120">120 min</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700">Notas</label>
            <textarea
              name="notes"
              rows="3"
              value={eventForm.notes}
              onChange={handleEventInputChange}
              className="mt-1 w-full rounded-lg border border-gray-300 p-2 text-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="Detalles relevantes para la visita o recordatorios internos"
            ></textarea>
          </div>
          <div className="md:col-span-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-gray-500">Se enviará confirmación al correo del cliente y el evento quedará en Google Calendar.</p>
            <button
              type="submit"
              disabled={eventLoading}
              className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {eventLoading ? 'Guardando...' : 'Guardar evento'}
            </button>
          </div>
        </form>

        <div className="mt-6">
          <h4 className="mb-3 text-sm font-semibold text-gray-700">Próximos eventos</h4>
          {eventsLoading ? (
            <p className="text-sm text-gray-500">Cargando eventos...</p>
          ) : events.length === 0 ? (
            <p className="text-sm text-gray-500">Aún no hay eventos registrados para este cliente.</p>
          ) : (
            <ul className="space-y-3">
              {events.map((event) => (
                <li key={event.id} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-medium text-gray-900">{event.title}</p>
                      <p className="text-xs text-gray-500">
                        {formatEventDate(event.start)} — {formatEventDate(event.end)}
                      </p>
                    </div>
                    {event.calendarLink && (
                      <a
                        href={event.calendarLink}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs font-semibold text-blue-600 hover:text-blue-700"
                      >
                        Ver evento
                      </a>
                    )}
                  </div>
                  {event.description && (
                    <p className="mt-2 text-sm text-gray-600">{event.description}</p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
};

export default ClientProfile;
